import * as React from 'react';
import axios from 'axios';
import { useAsyncDebounce } from 'react-table';
import client, { METHODS, ClientConfig } from 'api/client';
import { asyncWrapper, isFunction } from 'utils/common.utils';
import { API_ERROR_MESSAGE } from 'constants/common.constants';

export interface Props<T, Response> {
  initialUrl?: string | null;
  config?: ClientConfig;
  skipOnStart?: boolean;
  initialData?: T | null;
  onSuccess?: (data: T | null) => void;
  onFailure?: (error: string) => void;
  transform?: (response: Response) => T;
}

const UNMOUNTED_COMPONENT = 'component unmount';
const useFetch = <T, Response = unknown>({
  initialUrl = null,
  initialData = null,
  config = {},
  skipOnStart = false,
  onSuccess,
  onFailure,
  transform,
}: Props<T, Response>) => {
  const [url, updateUrl] = React.useState<string | null>(initialUrl);
  const [data, setData] = React.useState<null | T>(initialData);
  // in case of fetch on mount - set isLoading to true
  const [isLoading, setIsLoading] = React.useState(!skipOnStart);
  const [hasError, setHasError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<null | string>(null);
  const [fetchIndex, setFetchIndex] = React.useState(skipOnStart ? 0 : 1);

  const transformResponse = React.useCallback(
    (response: string) => {
      try {
        const parsedResponse: Response = JSON.parse(response);
        return transform?.(parsedResponse) ?? parsedResponse;
      } catch (error) {
        throw new Error('Error parsing response JSON data');
      }
    },
    [transform]
  );
  const [axiosConfig, updateConfig] = React.useState<ClientConfig>({
    ...config,
    ...(transform && { transformResponse }),
  });

  const updateView = React.useCallback((newConfig?: ClientConfig) => {
    setHasError(false);
    setIsLoading(true);

    if (newConfig) {
      updateConfig(prevConfig => ({
        ...prevConfig,
        ...newConfig,
      }));
    }

    setFetchIndex(prevFetchIndex => prevFetchIndex + 1);
  }, []);

  const callFetch = useAsyncDebounce(updateView, 200);

  React.useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const fetchData = async () => {
      const URL = url as string;
      const { data: response, error } = await asyncWrapper<T>(
        client({
          url: URL,
          cancelToken: cancelTokenSource.token,
          method: METHODS.GET,
          ...axiosConfig,
        })
      );
      if (!error) {
        setData(response);
        setHasError(false);
        setErrorMessage(null);
        setIsLoading(false);
        setFetchIndex(0);
        if (isFunction(onSuccess)) onSuccess(response);
      } else {
        const errorMsg = error?.message || API_ERROR_MESSAGE;
        // in case cancel token error - do not update react state to prevent memory leak
        if (errorMsg === UNMOUNTED_COMPONENT) return null;

        setHasError(true);
        setIsLoading(false);
        setFetchIndex(0);
        setErrorMessage(`error_${errorMsg}`);

        if (isFunction(onFailure)) onFailure(errorMsg);
      }
      return { response, error };
    };

    if (fetchIndex > 0 && url) {
      fetchData();
    }

    return () => {
      cancelTokenSource.cancel(UNMOUNTED_COMPONENT);
    };
  }, [url, axiosConfig, fetchIndex, onSuccess, onFailure]);

  return {
    data,
    isLoading,
    hasError,
    errorMessage,
    callFetch,
    updateUrl,
    updateConfig,
  };
};

export default useFetch;
